import { Component, Input, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TechfinanceService } from 'src/app/services/techfinance.service';
import { Observable, Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { InformationsComponent } from '../informations/informations.component';
import { ToastrService } from 'ngx-toastr';
import { ISettingState } from 'src/app/modules/settings/settings-general/store/setting.state';
import { setSettingAction } from 'src/app/modules/settings/settings-general/store/setting.action';
import { Store } from '@ngrx/store';
import { getSettingSelectors } from 'src/app/modules/settings/settings-general/store/setting.selector';

@Component({
  selector: 'app-studies',
  templateUrl: './studies.component.html',
  styleUrls: ['./studies.component.scss'],
})
export class StudiesComponent implements OnInit {
  @Input() clientId!: string;

  routeParam: string = '';
  userName: string;
  studiesData!: any[];
  paginatedStudies: any[] = [];
  hasStudies: boolean = false;
  isLoading: boolean = true;

  search: string = '';
  private searchSubject: Subject<string> = new Subject<string>();
  private destroy$ = new Subject<void>();
  filteredStudies: any[] = [];

  // PAGINAÇÃO
  currentPage: number = 1;
  totalPages!: number;
  pagesArray: number[] = [];
  pageSize: number = 5;

   dataStoreSetting$: Observable<any>;
  dataStoreSetting: ISettingState;

  constructor(
    private techfinanceService: TechfinanceService,
    private route: ActivatedRoute,
    private dialog: MatDialog,
    public toster: ToastrService,
    private router: Router,
    private store: Store
  ) {}

  ngOnInit(): void {
    const segment = this.route.snapshot.url[1]?.path;
    this.routeParam = segment || '';
    this.clientId = this.route.snapshot.paramMap.get('id')!;
    this.getState()
    this.getClientStudies();

    this.searchSubject
      .pipe(debounceTime(400), takeUntil(this.destroy$))
      .subscribe(() => {
        this.filterStudies();
      });
  }

  getState() {
      this.dataStoreSetting$ = this.store.select(getSettingSelectors);
      this.dataStoreSetting$.subscribe((value) => {
        this.dataStoreSetting = {
          ...value,
        };
      });
    }

  getClientStudies() {
    this.isLoading = true;
    this.techfinanceService
      .getGuiaStudies(this.clientId, this.routeParam)
      .subscribe((res: any) => {
        this.studiesData = res.data || [];
        this.filteredStudies = [...this.studiesData];
        this.hasStudies = this.studiesData.length > 0;
        this.totalPages = Math.ceil(
          this.filteredStudies.length / this.pageSize
        );
        this.updatePagesArray();
        this.paginateData();
        this.getSpecificUser();
        this.isLoading = false;
      });
  }

  filterStudies(): void {
    const term = this.search.toLowerCase().trim();

    if (term === '') {
      this.filteredStudies = [...this.studiesData];
    } else {
      this.filteredStudies = this.studiesData.filter((study) =>
        study.name?.toLowerCase().includes(term)
      );
    }

    this.currentPage = 1;
    this.totalPages = Math.ceil(this.filteredStudies.length / this.pageSize);
    this.updatePagesArray();
    this.paginateData();
  }

  onSearchChange(): void {
    this.searchSubject.next(this.search);
  }

  getSpecificUser() {
    this.techfinanceService
      .getEspecificUser(this.clientId)
      .subscribe((userlist: any) => {
        this.userName = `${userlist.firstName} ${userlist.lastName}`;
        document.cookie = `clientName=${this.userName}; path=/; domain=.techfinance.com.br`;
        this.dataStoreSetting.clientName = this.userName;
        this.setState(this.dataStoreSetting);
      });
  }

  accessStudy(studyId: number) {
    document.cookie = `studyId=${studyId}; path=/; domain=.techfinance.com.br`;
    this.router.navigate(['/presentation']);
  }

  createNewStudy() {
    this.techfinanceService
      .createStudy(this.clientId, this.routeParam)
      .subscribe((res: any) => {
        if (res.success) {
          localStorage.setItem('studyId', res.data.id);
          document.cookie = `studyId=${res.data.id}; path=/; domain=.techfinance.com.br`;
          this.router.navigate(['/home']);
        }
      });
  }

  paginateData() {
    const startIndex = (this.currentPage - 1) * this.pageSize;
    const endIndex = startIndex + this.pageSize;
    this.paginatedStudies = this.filteredStudies.slice(startIndex, endIndex);
  }

  updatePagesArray() {
    const maxVisiblePages = 5;
    let startPage = Math.max(
      this.currentPage - Math.floor(maxVisiblePages / 2),
      1
    );
    let endPage = startPage + maxVisiblePages - 1;

    if (endPage > this.totalPages) {
      endPage = this.totalPages;
      startPage = Math.max(endPage - maxVisiblePages + 1, 1);
    }

    this.pagesArray = [];
    for (let i = startPage; i <= endPage; i++) {
      this.pagesArray.push(i);
    }
  }

  goToPage(page: number) {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.updatePagesArray();
      this.paginateData();
    }
  }

  goToPreviousPage() {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.updatePagesArray();
      this.paginateData();
    }
  }

  goToNextPage() {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      this.updatePagesArray();
      this.paginateData();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  openInformations(data: any) {
    const dialogRef = this.dialog.open(InformationsComponent, {
      data: {
        study: data,
      },
      panelClass: 'custom-dialog-container',
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result.deleted) {
        this.toster.success(`estudo "${data.name}" foi excluído`);
        this.paginatedStudies = this.paginatedStudies.filter(
          (item) => item._id !== result.studyId
        );
      }
      if (result.updated) {
        this.toster.success(`Suas edições foram salvas`);
        const studyIndex = this.paginatedStudies.findIndex(
          (item) => item._id === result.study._id
        );
        if (studyIndex !== -1) {
          this.paginatedStudies[studyIndex].name = result.study.name;
        }
      }
    });
  }

  setState(data: ISettingState) {
    this.store.dispatch(setSettingAction(data));
  }
}
